import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import gettext from 'js-utils/gettext';

import {STYLE_BASIC} from 'eventbrite_design_system/inputField/constants';

import {
    SHARE_ON_EMAIL,
    SHARE_ON_FACEBOOK,
    SHARE_ON_FACEBOOK_MESSENGER,
    SHARE_ON_TWITTER,
} from 'eventbrite_design_system/shareBox/constants';
import {constants as destinationConstants} from '@eb/destination-utils';

import BusinessSvg from 'eventbrite_design_system/iconography/icons/Business';
import MusicNoteSvg from 'eventbrite_design_system/iconography/icons/MusicNote';
import MaskSvg from 'eventbrite_design_system/iconography/icons/Mask';
import BeakerSvg from 'eventbrite_design_system/iconography/icons/Beaker';
import ParkSvg from 'eventbrite_design_system/iconography/icons/Park';
import CocktailSvg from 'eventbrite_design_system/iconography/icons/Cocktail';
import FilmCameraSvg from 'eventbrite_design_system/iconography/icons/FilmCamera';
import ShoeSvg from 'eventbrite_design_system/iconography/icons/Shoe';
import HeartSvg from 'eventbrite_design_system/iconography/icons/Heart';
import TravelSvg from 'eventbrite_design_system/iconography/icons/Travel';
import CharitySvg from 'eventbrite_design_system/iconography/icons/Charity';
import ReligionSvg from 'eventbrite_design_system/iconography/icons/Religion';
import RockingHorseSvg from 'eventbrite_design_system/iconography/icons/RockingHorse';
import HolidaySvg from 'eventbrite_design_system/iconography/icons/Holiday';
import BookSvg from 'eventbrite_design_system/iconography/icons/Book';
import MegaphoneSvg from 'eventbrite_design_system/iconography/icons/Megaphone';
import FashionSvg from 'eventbrite_design_system/iconography/icons/Fashion';
import HouseSvg from 'eventbrite_design_system/iconography/icons/House';
import AutoSvg from 'eventbrite_design_system/iconography/icons/Auto';
import GameSvg from 'eventbrite_design_system/iconography/icons/Game';
import StarSvg from 'eventbrite_design_system/iconography/icons/Star';
import LogoInstagramSvg from 'eventbrite_design_system/iconography/icons/LogoInstagram';
import LogoTwitterSvg from 'eventbrite_design_system/iconography/icons/LogoTwitter';
import LogoLinkedinSvg from 'eventbrite_design_system/iconography/icons/LogoLinkedin';
import LogoFacebookSvg from 'eventbrite_design_system/iconography/icons/LogoFacebook';
import LogoPinterestSvg from 'eventbrite_design_system/iconography/icons/LogoPinterest';
import LogoGooglePlusSvg from 'eventbrite_design_system/iconography/icons/LogoGooglePlus';
import LinkChunkySvg from 'eventbrite_design_system/iconography/icons/LinkChunky';

export const {
    EVENTBRITE,
    TICKETFLY,
    GOLDSTAR,
    LINK_FOLLOW_WHITELIST,
    EVENT_TYPE,
    EVENTS_TYPE,
    ALL_EVENTS_TYPE,
    GOOGLE_MAPS_CLIENT_KEY,
    EVENT_CARD_TYPE,
    ARTICLE_CARD_TYPE,
    PROFILE_CARD_TYPE,
    CATEGORY_TYPE,
    NEARBY_CITY_TYPE,
    TRENDING_SEARCH_TYPE,
    ENTITY_TYPE_MAP,
    SINGULARIZED_ENTITY_TYPE_MAP,
    SEARCH_CRITERIA_TYPE_MAP,
    SEARCH_TYPE_MAP,
    EVENT_PAGE_PARAM,
    SEARCH_TYPE_PAGE_PARAM_MAP,
    EVENTBRITE_CATEGORY,
    MUSIC_EVENTBRITE_CATEGORY,
    FOOD_AND_DRINK_EVENTBRITE_CATEGORY,
    OTHER_EVENTBRITE_CATEGORY,
    CATEGORY_LONG_NAME_ICON_MAP,
    EVENTBRITE_CATEGORY_MAPPING,
    EVENTBRITE_CATEGORY_LOWERCASE_MAPPING,
    EVENTBRITE_CATEGORY_TO_REFINEMENT,
    CURRENT_FUTURE,
    CUSTOM_DATE,
    DATES_MAPPING,
    DATES_UPPERCASE_MAPPING,
    DATES_LOWERCASE_MAPPING,
    DATES_TO_REFINEMENT,
    FORMAT_TAG_TYPE,
    CATEGORY_TAG_TYPE,
    EVENTBRITE_FORMAT_MAPPING,
    EVENTBRITE_FORMAT_TO_REFINEMENT,
    PRICE_MAPPING,
    QUALITY,
    FORMATTED_CATEGORY_MAPPING,
    FORMATTED_DATE_MAPPING,
    FORMATTED_FORMAT_MAPPING,
    FORMATTED_PRICE_MAPPING,
    EVENT_PROPTYPES,
    BRANDING_PROPTYPE,
    PLACE_OBJECT_PROPTYPE,
    SERVER_SUGGESTIONS_PROPTYPE,
    SUGGESTIONS_PROPTYPE,
    LOCATION_SUGGESTION_PROPTYPE,
    BUCKET_API_RESPONSE_PROPTYPE,
    SEARCH_API_RESPONSE_PROPTYPE,
    SHARE_OPTIONS_PROPTYPE,
    ARTIST_SOCIAL_LINKS_PROPTYPE,
    DEFAULT_BLURB_NO_BUCKETS,
    DEFAULT_BLURB_NO_EVENTS,
    getDefaultBlurb,
    EVENT_SAVE,
    EVENT_SAVE_ERROR,
    CITY_NAME_SHORT_CUT,
    CITY_NAME_DEFAULT_CUT,
    CITY_GRADIENTS,
    SHORT_STATES,
    AUTOCOMPLETE_STORAGE_KEY,
    LOCATION_STORAGE_KEY,
    EVENT_COOKIE,
    CITY_CUTOFF,
    COLLECTION_TYPE,
} = destinationConstants;

const {
    BUSINESS,
    SCIENCE,
    MUSIC,
    FILM,
    PERFORMING,
    FASHION,
    HEALTH,
    SPORTS,
    TRAVEL,
    FOOD,
    CHARITY,
    POLITICS,
    COMMUNITY,
    RELIGION,
    FAMILY_EDUCATION,
    SEASONAL,
    HOME_LIFESTYLE,
    AUTO_BOAT_AIR,
    HOBBIES,
    SCHOOL,
    OTHER,
} = destinationConstants;

/****************************** APP LEVEL ********************************/

export const LUX_LABEL_SEARCH = 'Search';
export const LUX_LABEL_BROWSE = 'Browse';

export const LUX_MARK_BROWSE_READY = 'browse-page-ready';
export const LUX_MARK_SEARCH_READY = 'search-page-ready';
export const LUX_MARK_SEARCH_QUERIED = 'search-query-sent';
export const LUX_MARK_SEARCH_RENDERED = 'search-query-rendered';
export const LUX_MEASURE_SEARCH_RESPONSE_TIME = 'search-query-response-time';
export const LUX_MARK_MAP_RENDERED = 'search-map-rendered';

/****************************** SHARED CLASSES ********************************/

export const SHARED_BUCKET_SPACING_CLASSES = classNames(
    'content-bucket',
    'eds-l-pad-vert-20',
    'eds-l-sw-pad-vert-16',
    'eds-l-sm-pad-vert-16',
    'eds-l-sn-pad-vert-16'
);

export const NON_EVENT_BUCKET_SPACING_CLASSES = classNames(
    'content-bucket',
    'eds-l-pad-top-16',
    'eds-l-sw-pad-vert-16',
    'eds-l-sm-pad-vert-16',
    'eds-l-sn-pad-vert-16'
);

export const BUCKET_WRAPPER_WITH_DECORATION_CLASSES = classNames(
    'eds-l-mar-top-5',
    'eds-l-sm-mar-top-16',
    'eds-l-sn-mar-top-16'
);

export const TAG_BACKGROUND_COLOR = 'eds-bg-color--ui-orange';

/****************************** TEMPLATES AND CARD TYPES ********************************/

export const ARTICLE_TEMPLATE = 'article';
export const CALENDAR_TEMPLATE = 'calendar';
export const FEATURED_TEMPLATE = 'featured';
export const CATEGORY_TEMPLATE = 'category';
export const MAP_TEMPLATE = 'map';
export const NEARBY_CITIES_TEMPLATE = 'nearby_place';
export const TRENDING_SEARCHES_TEMPLATE = 'trending_searches';
export const COLLECTION_TEMPLATE = 'collection';

export const LARGE_CARD_TEMPLATE_3 = 'card_big_3';
export const LARGE_CARD_TEMPLATE_6 = 'card_big_6';
export const CARD_TEMPLATE_4 = 'card_small_4';
export const CARD_TEMPLATE_8 = 'card_small_8';
export const CARD_BIG = 'card_big';

const THIS_WEEKEND_BUCKETS_KEY = 'this_weekend';
const POPULAR_EVENTS_BUCKET_KEY = 'popular_events';
const CURATED_EVENTS_BUCKET_KEY = 'best_of_city_events';
const MUSIC_EVENTS_BUCKET_KEY = 'music_events';

export const EDITORIAL_ARTICLES_BUCKET_KEY = 'editorial_articles';
export const FREE_EVENTS_BUCKET_KEY = 'free_events';

/****************************** URLS ********************************/

export const BASE_URL = '/d';
export const BROWSE_URL = ':location/events/';
export const SEARCH_URL = ':location/:refinementOne(/:refinementTwo)/';

export const SEARCH_RELEVANT_QUERY_PARAMS = ['start_date', 'end_date', EVENT_PAGE_PARAM, 'src', 'bbox', 'tags'];

/****************************** CATEGORY ID MAPPING ********************************/

//Very TBD, will be replaced once official
//category graphics are put together.
export const CATEGORY_NAME_ICON_MAP = {
    [BUSINESS]: <BusinessSvg />,
    [MUSIC]: <MusicNoteSvg />,
    [PERFORMING]: <MaskSvg />,
    [SCIENCE]: <BeakerSvg />,
    [COMMUNITY]: <ParkSvg />,
    [FOOD]: <CocktailSvg />,
    [FILM]: <FilmCameraSvg />,
    [SPORTS]: <ShoeSvg />,
    [HEALTH]: <HeartSvg />,
    [TRAVEL]: <TravelSvg />,
    [CHARITY]: <CharitySvg />,
    [RELIGION]: <ReligionSvg />,
    [FAMILY_EDUCATION]: <RockingHorseSvg />,
    [SEASONAL]: <HolidaySvg />,
    [SCHOOL]: <BookSvg />,
    //Backup: 'trash'
    [POLITICS]: <MegaphoneSvg />,
    [FASHION]: <FashionSvg />,
    [HOME_LIFESTYLE]: <HouseSvg />,
    [AUTO_BOAT_AIR]: <AutoSvg />,
    [HOBBIES]: <GameSvg />,
    [OTHER]: <StarSvg />,
};

export const CATEGORY_DROPDOWN_SELECT = 'category_select';

/****************************** FILTER PROPS MAP *****************************/

const TAG_PLACEHOLDER_TEXT = gettext('Any category');
const DATE_PLACEHOLDER_TEXT = gettext('Any date');
const FORMAT_PLACEHOLDER_TEXT = gettext('Any event type');
const PRICE_PLACEHOLDER_TEXT = gettext('Any price');

export const CLEAR_PRICE_FILTER = 'clearPrice';

export const FACETED_SEARCH_DROPDOWN_FILTER_PROP_MAP = {
    tags: {
        id: 'category-select',
        label: gettext('Category'),
        values: [
            {
                display: TAG_PLACEHOLDER_TEXT,
                value: '',
            },
            ...FORMATTED_CATEGORY_MAPPING,
        ],
        style: STYLE_BASIC,
    },
    dates: {
        id: 'date-select',
        label: gettext('Date'),
        values: [
            {
                display: DATE_PLACEHOLDER_TEXT,
                value: '',
            },
            ...FORMATTED_DATE_MAPPING,
        ],
        style: STYLE_BASIC,
    },
    format: {
        id: 'format-select',
        label: gettext('Event type'),
        values: [
            {
                display: FORMAT_PLACEHOLDER_TEXT,
                value: '',
            },
            ...FORMATTED_FORMAT_MAPPING,
        ],
        style: STYLE_BASIC,
    },
    price: {
        id: 'price-select',
        label: gettext('Price'),
        values: [
            {
                display: PRICE_PLACEHOLDER_TEXT,
                value: CLEAR_PRICE_FILTER,
            },
            ...FORMATTED_PRICE_MAPPING,
        ],
        style: STYLE_BASIC,
    },
};

export const DROPDOWN_FILTER_PROP_MAP = {
    tags: {
        placeholder: TAG_PLACEHOLDER_TEXT,
        id: 'category-select',
        label: gettext('Category'),
        values: FORMATTED_CATEGORY_MAPPING,
        style: STYLE_BASIC,
    },
    dates: {
        placeholder: DATE_PLACEHOLDER_TEXT,
        id: 'date-select',
        label: gettext('Date'),
        values: FORMATTED_DATE_MAPPING,
        style: STYLE_BASIC,
    },
    format: {
        placeholder: FORMAT_PLACEHOLDER_TEXT,
        id: 'format-select',
        label: gettext('Event type'),
        values: FORMATTED_FORMAT_MAPPING,
        style: STYLE_BASIC,
    },
    price: {
        placeholder: PRICE_PLACEHOLDER_TEXT,
        id: 'price-select',
        label: gettext('Price'),
        values: FORMATTED_PRICE_MAPPING,
        style: STYLE_BASIC,
    },
};

/****************************** GA CONSTANTS ********************************/

export const CITY_BROWSE_SECTION = 'cityBrowse';
export const SEARCH_SECTION = 'search';
export const ONLINE_BROWSE_SECTION = 'onlineBrowse';
export const ONLINE_SEARCH_SECTION = 'onlineSearch';

export const SEARCH_EVENT_NAVIGATION = 'search.event';

export const GA_CITY_BROWSE_CATEGORY = 'city-browse';
export const GA_SEARCH_CATEGORY = 'search';
export const GA_CATEGORY_MAP = {
    cityBrowse: GA_CITY_BROWSE_CATEGORY,
    search: GA_SEARCH_CATEGORY,
};

export const GA_TERM_MAP = {
    cityBrowse: 'citybrowse',
    search: 'destsearch',
};

export const GA_SEARCH_EVENT_CARD_CATEGORY = 'eventcard-search';
export const GA_CB_EVENT_CARD_CATEGORY = 'eventcard-cb';
export const GA_EVENT_CARD_CATEGORY_MAP = {
    cityBrowse: GA_CB_EVENT_CARD_CATEGORY,
    search: GA_SEARCH_EVENT_CARD_CATEGORY,
};

export const GA_VIEW_EVENT_ACTION = 'ViewEvent';
export const GA_EXTERNAL_VIEW_EVENT_ACTION = 'ViewExternal';
export const GA_VIEW_ARTICLE_ACTION = 'ViewArticle';
export const GA_VIEW_PROMOTED_EVENT_ACTION = 'ViewEventPromoted';
export const GA_LOCATION_CHANGE_ACTION = 'LocChange';
export const GA_VIEW_MORE_ACTION = 'ViewMore';
export const GA_SCROLL_DEPTH_ACTION = 'ScrollDepth';
export const GA_VIEW_CATEGORY_ACTION = 'ViewCategory';
export const GA_VIEW_NEARBY_CITY_ACTION = 'ViewNearbyCity';
export const GA_SAVE_EVENT_ACTION = 'Bookmark';
export const GA_UNSAVE_EVENT_ACTION = 'Unbookmark';
export const GA_SHARE_ATTEMPT_ACTION = 'ShareAttempt';
export const GA_SHARE_ACTION = 'Share';
export const GA_CHANGE_PAGE_ACTION = 'ChangePage';
export const GA_CATEGORY_FILTER_ACTION = 'CategoryFilter';
export const GA_EVENT_TYPE_FILTER_ACTION = 'EventTypeFilter';
export const GA_PRICE_FILTER_ACTION = 'PriceFilter';
export const GA_DATE_FILTER_ACTION = 'DateFilter';
export const GA_RUN_SEARCH_ACTION = 'RunSearch';
export const GA_VIEW_TRENDING_SEARCH_ACTION = 'TrendingSearch';
export const GA_VIEW_ALL_TRENDING_SEARCHES_ACTION = 'AllTrendingSearches';
export const GA_VIEW_EVENT_AUTOCOMPLETE = 'ViewEventAutocomplete';
export const GA_REDO_FROM_MAP = 'RedoFromMap';
export const GA_ENTER_MAP_VIEW = 'EnterMapView';
export const GA_VIEW_EVENT_MAP_VIEW = 'ViewEventMapView';
export const GA_SEARCH_TAG_CLICK_ACTION = 'SearchTagClick';
export const GA_SEARCH_TAG_CLEAR_ACTION = 'SearchTagClear';
export const GA_MORE_FILTERS_PANEL_ACTION = 'MoreFiltersPanel';
export const GA_MORE_FILTERS_CLEARED_ACTION = 'MoreFiltersCleared';
export const GA_CREATE_COLLECTION_VIEW_ACTION = 'CreateCollectionAttempt';
export const GA_ADD_TO_COLLECTION_SHOW_ACTION = 'AddToCollectionListShown';
export const GA_SCROLL_COLLECTIONS_ACTION = 'ScrollCollections';
export const GA_CLICK_VIEW_COLLECTION_ACTION = 'ViewCollection';
// this is not an actual action, but it's used in analytics logging in the same place where the above actions are used
export const GA_PAGE_LOAD_ACTION = 'PageLoad';


export const GA_SCROLL_COMPLETE_LABEL = '100';
export const GA_SCROLL_NA_LABEL = 'N/A';

const GA_TWITTER_IDENTIFIER = 'tw';
const GA_FACEBOOK_IDENTIFIER = 'fb';
const GA_FACEBOOK_MESSENGER_IDENTIFIER = 'fbm';
const GA_EMAIL_IDENTIFIER = 'em';
const GA_COPY_IDENTIFIER = 'cp';

export const SHARE_BY_COPY = 'copy';
export const SHARE_TYPE_GA_MAP = {
    [SHARE_ON_EMAIL]: GA_EMAIL_IDENTIFIER,
    [SHARE_ON_FACEBOOK]: GA_FACEBOOK_IDENTIFIER,
    [SHARE_ON_FACEBOOK_MESSENGER]: GA_FACEBOOK_MESSENGER_IDENTIFIER,
    [SHARE_ON_TWITTER]: GA_TWITTER_IDENTIFIER,
    [SHARE_BY_COPY]: GA_COPY_IDENTIFIER,
};

/*Filter Types*/
export const CATEGORY_FILTER = 'tags';
export const FORMAT_FILTER = 'format';
export const PRICE_FILTER = 'price';
export const DATE_FILTER = 'dates';
export const QUERY_FILTER = 'q';

export const FACET_FILTER = 'masterTags';

export const FILTER_TYPE_GA_MAP = {
    [CATEGORY_FILTER]: GA_CATEGORY_FILTER_ACTION,
    [FORMAT_FILTER]: GA_EVENT_TYPE_FILTER_ACTION,
    [PRICE_FILTER]: GA_PRICE_FILTER_ACTION,
    [DATE_FILTER]: GA_DATE_FILTER_ACTION,
    [QUERY_FILTER]: GA_RUN_SEARCH_ACTION,
};


/****************************** JANUS CONSTANTS ********************************/

export const AGGREGATION_EXPERIMENT = 'exp_eb_81052_la_aggregation';

export const CITY_BROWSE_EVENT_CLICKED = 'city_browse_event_clicked';
export const CITY_BROWSE_MANUAL_EVENT_CLICKED = 'city_browse_manual_event_clicked';
export const CITY_BROWSE_EVENT_BOOKMARKED = 'city_browse_event_bookmarked';

export const DESTINATION_SEARCH_EVENT_BOOKMARKED_CHECKPOINT = 'destination_search_event_bookmarked';
export const DESTINATION_SEARCH_EVENT_CLICKED_CHECKPOINT = 'destination_search_event_clicked';
export const DESTINATION_MANUAL_SEARCH_EVENT_CLICKED_CHECKPOINT = 'destination_search_manual_event_clicked';

export const SEARCH_TAG_CLICKED = 'search_tag_clicked';
export const SEARCH_TAG_CLEARED = 'search_tag_cleared';

export const NON_MANUAL_SOURCES = [TICKETFLY, EVENTBRITE, GOLDSTAR];
export const AGG_EXPERIMENT_LA_METRO_WHITELIST = [
    'ca--los-angeles',
    'ca--west-hollywood',
    'ca--santa-monica',
    'ca--long-beach',
    'ca--burbank',
    'ca--beverly-hills',
    'ca--riverside',
    'ca--glendale',
    'ca--culver-city',
    'ca--marina-del-rey',
];

export const EXPERIMENTS_SHAPE_PROP_TYPE = PropTypes.shape({
    'event_cards': PropTypes.string,
});

/****************************** TRACKING CONSTANTS ********************************/

const CITY_BROWSE_EVENT_VIEW_AFFILIATE = 'ebdssbcitybrowse';
const SEARCH_EVENT_VIEW_SEARCH_AFFILIATE = 'ebdssbdestsearch';
const ONLINE_BROWSE_EVENT_VIEW_AFFILIATE = 'ebdssbonlinebrowse';
const ONLINE_SEARCH_EVENT_VIEW_AFFILIATE = 'ebdssbonlinesearch';

export const EVENT_AUTOCOMPLETE_AFFILIATE = 'ebdssbeac';
export const EVENT_AFFILIATES_MAP = {
    [CITY_BROWSE_SECTION]: CITY_BROWSE_EVENT_VIEW_AFFILIATE,
    [SEARCH_SECTION]: SEARCH_EVENT_VIEW_SEARCH_AFFILIATE,
    [ONLINE_BROWSE_SECTION]: ONLINE_BROWSE_EVENT_VIEW_AFFILIATE,
    [ONLINE_SEARCH_SECTION]: ONLINE_SEARCH_EVENT_VIEW_AFFILIATE,
};

/****************************** IMAGES ********************************/

export const DECORATIVE_IMAGE_PLACEMENT_MAP = [
    {
        selectors: [POPULAR_EVENTS_BUCKET_KEY, CURATED_EVENTS_BUCKET_KEY],
        urlSelector: 'decorationTopRightUrl',
        position: 'right',
        classes: 'decorative-image--one',
        parentClasses: 'eds-l-pad-top-6',
    },
    {
        selectors: [THIS_WEEKEND_BUCKETS_KEY],
        urlSelector: 'decorationTopLeftUrl',
        position: 'left',
        classes: 'decorative-image--two',
        parentClasses: 'eds-l-pad-top-12',
    },
    {
        selectors: [EDITORIAL_ARTICLES_BUCKET_KEY, MUSIC_EVENTS_BUCKET_KEY],
        urlSelector: 'decorationBottomRightUrl',
        position: 'right',
        classes: 'decorative-image--three',
        parentClasses: 'eds-l-pad-top-6',
    },
    {
        selectors: [FREE_EVENTS_BUCKET_KEY],
        urlSelector: 'decorationBottomLeftUrl',
        position: 'left',
        classes: 'decorative-image--four',
        parentClasses: 'eds-l-pad-top-6',
    },
];

/****************************** Artist Social Link Tye Constants ********************************/

export const SOCIAL_TYPE_ICON_MAP = {
    'instagram': <LogoInstagramSvg />,
    'twitter': <LogoTwitterSvg />,
    'linkedin': <LogoLinkedinSvg />,
    'facebook': <LogoFacebookSvg />,
    'pinterest': <LogoPinterestSvg />,
    'google-plus': <LogoGooglePlusSvg />,
    'web': <LinkChunkySvg />,
};

export const HEADER_HEIGHT = 72;
